<template>
  <section v-if="expense" class="details-header">
    <div class="header-title">
      <div class="sub-header">
        {{ $t('expenses.details.expense') }}
        #{{ expense.referenceNumber }}
      </div>
      <div>
        <Status :status="expense.status" object-type="expense" />
      </div>
      <div class="expense-minor-actions">
        <TolButton
          :tooltip="$t('expense.actions.saveAsPdf')"
          button-type="minor"
          icon-name="file-pdf"
          class="action-round"
          @click="exportAsPdf"
        />
        <template v-if="canSave">
          <TolButton
            :tooltip="$t('expense.actions.save')"
            button-type="minor"
            icon-name="pencil-alt"
            class="action-round"
            @click="$emit('save-expense')"
          />
        </template>
        <template v-if="canReopen">
          <TolButton
            :tooltip="$t('expense.actions.reopen')"
            button-type="minor"
            icon-name="lock-open"
            class="action-round"
            @click="$emit('reopen-expense')"
          />
        </template>
      </div>
    </div>
    <CreatedComment :comment="expense.approverComment" />
    <hr />
    <div class="header-rows">
      <div class="header-info">
        <div class="header-label">{{ $t('expense.labels.contractor') }}</div>
        <div class="header-value">
          <router-link
            class="contacts-list-link"
            :to="`/contractors/${expense.assignment.contractor.reference_number}`"
          >
            <ContactsList :contacts="[expense.contractor]" />
          </router-link>
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('expense.labels.payrollNum') }}</div>
        <div class="header-value">
          <span class="nowrap">{{ expense.assignment.payroll_number }}</span>
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('expense.labels.client') }}</div>
        <div class="header-value">
          <router-link :to="`/clients/${expense.assignment.client_reference_number}`">
            <ClientsDisplay :contacts="[{ lastName: expense.assignment.client_name }]" />
          </router-link>
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('expense.labels.period') }}</div>
        <div class="header-value">
          <span class="nowrap">{{ expense.startDate | formatDate }}</span>
          to
          <span class="nowrap">{{ expense.endDate | formatDate }}</span>
        </div>
      </div>
      <div class="header-info">
        <div class="header-label">{{ $t('expense.labels.approvers') }}</div>
        <div class="header-value approver-name">
          <ContactsList :contacts="expense.approvers" contact-type="approver" />
        </div>
      </div>
      <div class="header-info assignment">
        <div class="header-label">{{ $t('expense.labels.assignment') }}</div>
        <div class="header-value">
          <router-link
            class="clients-display-link"
            :to="`/assignments/${expense.assignment.reference_number}`"
          >
            <AssignmentDisplayLink :assignment="expense.assignment" />
          </router-link>
        </div>
      </div>
      <!-- Splitting last bit -->
    </div>
    <VueHtml2pdf
      ref="ExpenseDownloadComp"
      :show-layout="false"
      :paginate-elements-by-height="1400"
      pdf-format="a4"
      :manual-pagination="false"
      :enable-download="false"
      :preview-modal="false"
      :filename="`Expense-${expense.referenceNumber}`"
      @beforeDownload="beforeDownload($event)"
    >
      <section slot="pdf-content">
        <ExpenseDetailsPdf :expense="expense" />
      </section>
    </VueHtml2pdf>
  </section>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf';
import format from 'date-fns/format';
import ExpenseDetailsPdf from './ExpenseDetailsPdf.vue';
import ContactsList from '@/app/components/ContactsList';
import TolButton from '@/app/components/TolButton';
import AssignmentDisplayLink from '@/app/components/AssignmentDisplayLink';
import Status from '@/app/components/Status';
import ClientsDisplay from '@/app/components/ClientsDisplay';
import CreatedComment from '@/app/components/CreatedComment';

export default {
  name: 'ExpenseBodyHeader',
  components: {
    TolButton,
    ContactsList,
    ClientsDisplay,
    Status,
    CreatedComment,
    AssignmentDisplayLink,
    ExpenseDetailsPdf,
    VueHtml2pdf
  },
  props: {
    expense: {
      type: Object,
      required: true
    },
    canSave: {
      type: Boolean,
      required: true
    },
    canReopen: {
      type: Boolean,
      required: true
    }
  },
  methods: {
    exportAsPdf() {
      this.$refs.ExpenseDownloadComp.generatePdf();
    },
    async beforeDownload({ html2pdf, options, pdfContent }) {
      await html2pdf()
        .set(options)
        .from(pdfContent)
        .toPdf()
        .get('pdf')
        .then(pdf => {
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(10);
            pdf.setTextColor(150);
            pdf.text(
              'Generated by TimeOnline at ' + format(new Date(), 'YYYY-MM-DD HH:M'),
              pdf.internal.pageSize.getWidth() * 0.05,
              pdf.internal.pageSize.getHeight() - 0.3
            );
            pdf.text(
              'Page ' + i + ' of ' + totalPages,
              pdf.internal.pageSize.getWidth() * 0.88,
              pdf.internal.pageSize.getHeight() - 0.3
            );
          }
        })
        .save();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/variables.scss';
@import '../../../../../styles/colors.scss';

.details-header {
  background-color: $background-level-2;
  .header-title {
    padding: $details-padding;
    display: flex;
    align-items: center;

    .expense-minor-actions {
      flex-grow: 1;
      text-align: right;
    }
    .button-separator {
      margin: 0 ($details-padding/2);
      display: inline-block;
    }
  }
  .header-rows {
    padding: $details-padding;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }
  .header-info {
    padding: 10px 5px;
    font-weight: bold;
    &.assignment {
      font-weight: normal;
    }
  }
  .header-label {
    text-transform: uppercase;
    font-size: 15px;
    font-weight: bold;
    line-height: 19px;
    color: $text-color-dark-2;
  }
  .header-value a {
    text-decoration: none;
    color: inherit;
  }
}

@media only screen and (max-width: 768px) {
  .details-header {
    font-size: 0.8rem;
    .header-rows {
      display: flex;
      flex-direction: column;
      padding: $responsive-header-padding-sm;
      .header-info {
        width: 100%;
      }
    }

    .header-label {
      font-size: 0.8rem;
      margin-bottom: 5px;
    }

    .header-value {
      font-size: 0.8rem;
    }
  }
}
</style>
